// @ts-nocheck
/*
 * Port of voice-library/moh-management.js (481 lines). BYTE-IDENTICAL.
 * MOH list with per-row Listen/Download/Approve/Reject/Set-Default. Only
 * SUPERADMIN sees Approve/Reject buttons (Approved status = 1). Set
 * Default is gated by moh_status === 1 (Approved). Multi-select delete
 * via the reusable RenderDeleteModal.
 * PrimeReact `DataTable` (server has all rows already) — obd3-gui
 * DataTable + tanstack ColumnDef; PrimeReact `Checkbox` inlined as a
 * native <input type="checkbox">; DeleteIcon inlined (same shape as
 * remarks/RemarkStatusListPage). v1 LottieAnimation toast — dropped
 * (v2 uses react-toastify + bare Modal for the info dialog).
 */
import React, { useState, useEffect } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('card-text', className)} {...props}>
    {children}
  </p>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

import { getApiCall, postApiCall } from '@/service/httpCall';
import { storageUrlOr } from '@/utilities/storageUrl';
import Loader from '@/components/partials/components/Loader';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { canUploadMoh } from '@/utilities/promptUploadAccess';
import { USER_ROLES, MODULE_ACL, ROUTES } from '@/utilities/constant';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { Plus, Trash2, RefreshCw, Star, Download, Play, Check, X, ExternalLink } from 'lucide-react';
import {
  API_MOH_LIST,
  API_MOH_DELETE,
  API_MOH_UPDATE_STATUS,
  API_MOH_SET_DEFAULT,
} from '@/service/url';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const DeleteIcon = ({ handleCallback }: any) => (
  <Link
    className="btn btn-sm btn-icon btn-danger"
    title="Delete"
    to="#"
    onClick={(e) => {
      e.preventDefault();
      handleCallback();
    }}
  >
    <span className="btn-inner">
      <svg
        width="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        stroke="currentColor"
      >
        <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

const MOH_STATUS_LABEL: any = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
};

const MohManagement = () => {
  const [mohList, setMohList] = useState<any[]>([]);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');

  const [selectedData, setSelected] = useState<any[]>([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(undefined);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [reloadTrigger, setReloadTrigger] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [acl, setAcl] = useState<any>({});
  const [defaultLoadingId, setDefaultLoadingId] = useState<any>(null);
  const [, setToastProps] = useState<any>(null);
  const [, setShowToast] = useState(false);

  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      try {
        setAcl(parseModuleOperations(user.acl_settings.module_operations));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchMoh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  useEffect(() => {
    if (searchValue && searchValue.trim() !== '') {
      const q = searchValue.trim().toLowerCase();
      setMohList(
        originalDataList.filter((d) => d.moh_name?.toLowerCase().includes(q)),
      );
    } else {
      setMohList(originalDataList);
    }
  }, [searchValue, originalDataList]);

  // React to ACL refresh elsewhere (matches manage-prompts pattern).
  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setAcl(parsed);
        } catch (e) {
          // ignore
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchMoh = () => {
    setLoading(true);
    getApiCall(API_MOH_LIST)
      .then((resp: any) => {
        const arr = Array.isArray(resp) ? resp : resp?.data || [];
        const list = arr.map((item: any, idx: number) => {
          const groups = Array.isArray(item.allocatedCallGroups)
            ? item.allocatedCallGroups
            : [];
          return {
            ...item,
            key: idx + 1,
            moh_url: storageUrlOr('moh', item?.blob_path, item?.moh_url),
            status: MOH_STATUS_LABEL[item.moh_status] || 'Pending',
            allocated_groups_label: groups.length
              ? groups.map((g: any) => g.group_name).join(', ')
              : '-',
            allocated_count: groups.length,
          };
        });
        setOriginalDataList(list);
        setMohList(list);
      })
      .catch((err: any) => toast.warning(friendlyApiError(err)))
      .finally(() => setLoading(false));
  };

  const showToasts = (msg: string) => {
    toast.success(msg);
  };

  const handleApprove = (id: any) => {
    postApiCall(API_MOH_UPDATE_STATUS.replace(':id', id), { status: 1 })
      .then(() => {
        showToasts('MOH Approved');
        setReloadTrigger((p) => !p);
      })
      .catch((err: any) => toast.error(friendlyApiError(err)));
  };

  const handleReject = (id: any) => {
    postApiCall(API_MOH_UPDATE_STATUS.replace(':id', id), { status: 2 })
      .then(() => {
        showToasts('MOH Rejected');
        setReloadTrigger((p) => !p);
      })
      .catch((err: any) => toast.error(friendlyApiError(err)));
  };

  const handleListen = (url: string) => {
    setAudioUrl(url);
    setIsModalVisible(true);
  };
  const handleModalClose = () => {
    setIsModalVisible(false);
    setAudioUrl('');
  };

  const handleSelectAll = () => {
    if (selectedData.length === mohList.length) setSelected([]);
    else setSelected(mohList.map((m: any) => m.id));
  };
  const handleCheckboxToggle = (id: any) => {
    setSelected(
      selectedData.includes(id)
        ? selectedData.filter((x: any) => x !== id)
        : [...selectedData, id],
    );
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };
  const handleDelete = (id: any) => {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteId(id);
  };

  useEffect(() => {
    if (!confirmDelete) return;
    const ids = isMultiDelete ? selectedData : [deleteId];
    postApiCall(API_MOH_DELETE, { mohIds: ids })
      .then((resp: any) => {
        toast.success(resp);
        setSelected([]);
        setEnableDeleteModal(false);
        setDeleteId(undefined);
        setConfirmDelete(false);
        fetchMoh();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.warning(`Unable to delete MOH: ${err}`);
        }
        setConfirmDelete(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete]);

  const downloadFile = async (fileUrl: string, filename?: string) => {
    try {
      const resp = await fetch(fileUrl);
      if (!resp.ok) throw new Error('Failed to fetch file');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || fileUrl.split('/').pop() || 'moh';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const canDelete = isSuperAdmin || acl?.[MODULE_ACL?.MOH_MANAGEMENT]?.delete;
  const canAdd = canUploadMoh() && (isSuperAdmin || acl?.[MODULE_ACL?.MOH_MANAGEMENT]?.add);

  const handleSetDefault = (rowData: any) => {
    if (!rowData?.id) return;
    if (rowData?.is_default) return;
    if (Number(rowData?.moh_status) !== 1) {
      toast.warning('Only approved MOH can be marked as default');
      return;
    }
    setDefaultLoadingId(rowData.id);
    postApiCall(API_MOH_SET_DEFAULT.replace(':id', rowData.id), {})
      .then(() => {
        toast.success('Default MOH updated');
        setReloadTrigger((p) => !p);
      })
      .catch((err: any) =>
        toast.error(
          typeof err === 'string' ? err : err?.message || 'Unable to set default MOH',
        ),
      )
      .finally(() => setDefaultLoadingId(null));
  };

  const defaultBodyTemplate = (rowData: any) =>
    rowData?.is_default ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
        <Star className="h-3 w-3 fill-current" />
        Default
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const actionBodyTemplate = (rowData: any) => {
    const isApproved = rowData.status === 'Approved';
    const isRejected = rowData.status === 'Rejected';
    const isDeleted =
      rowData?.is_deleted === true ||
      Number(rowData?.is_deleted) === 1 ||
      Boolean(rowData?.deleted_at);

    const canBeDefault = Number(rowData?.moh_status) === 1 && !isDeleted;

    const iconBtn =
      'inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition-colors';
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          title={
            rowData?.is_default
              ? 'Default MOH'
              : canBeDefault
              ? 'Set as default'
              : 'Only approved MOH can be default'
          }
          aria-label="Set as default"
          disabled={rowData?.is_default || !canBeDefault || defaultLoadingId === rowData.id}
          onClick={(e) => {
            e.stopPropagation();
            handleSetDefault(rowData);
          }}
          className={
            iconBtn +
            ' ' +
            (rowData?.is_default
              ? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
              : 'bg-primary/10 text-primary ring-primary/20 hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40')
          }
        >
          <Star
            className={'h-3.5 w-3.5 ' + (rowData?.is_default ? 'fill-current' : '')}
          />
        </button>

        <button
          type="button"
          title="Listen"
          aria-label="Listen"
          onClick={(e) => {
            e.stopPropagation();
            handleListen(rowData.moh_url);
          }}
          className={iconBtn + ' bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'}
        >
          <Play className="h-3.5 w-3.5" />
        </button>

        <button
          type="button"
          title="Download"
          aria-label="Download"
          onClick={(e) => {
            e.stopPropagation();
            downloadFile(rowData.moh_url, rowData.moh_name);
          }}
          className={iconBtn + ' bg-primary/10 text-primary ring-primary/20 hover:bg-primary/15'}
        >
          <Download className="h-3.5 w-3.5" />
        </button>

        {isSuperAdmin && (
          <>
            <button
              type="button"
              title="Approve"
              aria-label="Approve"
              disabled={isApproved}
              onClick={(e) => {
                e.stopPropagation();
                !isApproved && handleApprove(rowData.id);
              }}
              className={
                iconBtn +
                ' bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 disabled:cursor-not-allowed disabled:opacity-40'
              }
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Reject"
              aria-label="Reject"
              disabled={isRejected}
              onClick={(e) => {
                e.stopPropagation();
                !isRejected && handleReject(rowData.id);
              }}
              className={
                iconBtn +
                ' bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300 disabled:cursor-not-allowed disabled:opacity-40'
              }
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        {canDelete && !isDeleted && (
          <button
            type="button"
            title="Delete"
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(rowData?.id);
            }}
            className={
              iconBtn +
              ' bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={selectedData.length === mohList.length && mohList.length > 0}
          onChange={handleSelectAll}
        />
      ),
      enableSorting: false,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedData.includes(row.original.id)}
          onChange={() => handleCheckboxToggle(row.original.id)}
        />
      ),
    },
    { accessorKey: 'moh_name', header: 'Name' },
    {
      id: 'is_default',
      header: 'Default',
      enableSorting: false,
      cell: ({ row }: any) => defaultBodyTemplate(row.original),
    },
    {
      accessorKey: 'created_at',
      header: 'Created Date',
      cell: ({ row }: any) =>
        row.original.created_at
          ? new Date(row.original.created_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-',
    },
    {
      accessorKey: 'moh_url',
      header: 'File URL',
      enableSorting: false,
      cell: ({ row }: any) => {
        const u = row.original?.moh_url;
        if (!u) return <span className="text-muted-foreground">—</span>;
        return (
          <a
            href={u}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
            title={u}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View
          </a>
        );
      },
    },
    { accessorKey: 'moh_duration', header: 'Duration (s)' },
    {
      accessorKey: 'allocated_groups_label',
      header: 'Allocated To (Call Groups)',
    },
    {
      accessorKey: 'moh_type',
      header: 'Type',
      cell: ({ row }: any) => {
        const t = String(row.original?.moh_type || '').toLowerCase();
        if (!t) return <span className="text-muted-foreground">—</span>;
        const tone =
          t === 'incoming'
            ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300'
            : t === 'outgoing'
            ? 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300'
            : 'bg-primary/10 text-primary ring-primary/25';
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ' +
              tone
            }
          >
            {row.original.moh_type}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => {
        const s = String(row.original?.status || '').toLowerCase();
        const tone =
          s === 'approved'
            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            : s === 'rejected'
            ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
            : 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              tone
            }
          >
            {row.original.status || 'Pending'}
          </span>
        );
      },
    },
    {
      id: 'creator',
      header: 'Created By',
      cell: ({ row }: any) =>
        row.original?.creator?.name || row.original?.creator?.username || '-',
    },
    {
      id: 'action',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: any) => actionBodyTemplate(row.original),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="MOH Management"
        description="Music-on-hold files that play while callers are waiting."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PageButton
              onClick={() => setReloadTrigger((p) => !p)}
              size="sm"
              variant="outline"
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </PageButton>
            {canAdd && (
              <PageButton
                onClick={() => navigate(`/${ROUTES.VOICE_LIBRARY.ADD_MOH}`)}
                size="sm"
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add MOH
              </PageButton>
            )}
            {canDelete && (
              <PageButton
                variant="destructive"
                size="sm"
                onClick={handleMultiDelete}
                disabled={selectedData.length === 0}
                className="gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete Selected
              </PageButton>
            )}
          </div>
        }
      />
      <div className="mt-4">
        <div className="scroll-container">
          <DataTable
            tableId="moh-management:v2"
            columns={columns}
            data={mohList}
            pageSize={10}
            emptyTitle="No MOH files"
            disableExport
          />
        </div>

        <RenderDeleteModal
          show={enableDeleteModal}
          setShow={setEnableDeleteModal}
          setConfirmDelete={setConfirmDelete}
          isMultiDelete={isMultiDelete}
          clearSelectedData={() => setSelected([])}
        />
      </div>

      {/* Audio playback dialog */}
      <Modal show={isModalVisible} onHide={handleModalClose} centered>
        <Modal.Header>
          <Modal.Title className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              <Play className="h-4 w-4" />
            </span>
            MOH Playback
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <audio controls autoPlay className="w-full">
            <source src={audioUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </Modal.Body>
      </Modal>
    </FillPage>
  );
};

export default MohManagement;
